import { useState, useRef, useMemo } from "react";
import { X } from "lucide-react";
import "./CategorySelect.css";

const SUGGESTED_CATEGORIES = [
  "Fashion & Accessories",
  "Jewellery",
  "Art & Prints",
  "Home & Lifestyle",
  "Handmade Goods",
  "Beauty & Skincare",
  "Food & Drink",
  "Bakery & Desserts",
  "Coffee & Tea",
  "Kids & Toys",
  "Books & Stationery",
  "Plants & Florals",
  "Candles & Fragrance",
  "Vintage & Thrift",
  "Leather Goods",
  "Ceramics & Pottery",
  "Textiles & Weaving",
  "Wellness",
];

export default function CategorySelect({ value = [], onChange, placeholder = "Add a category" }) {
  const [inputValue, setInputValue] = useState("");
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);

  // Normalize array elements in case raw strings are passed in
  const normalizedValue = useMemo(() => {
    return (Array.isArray(value) ? value : []).map((item) => {
      if (typeof item === "string") return { label: item, value: item };
      return { label: item?.label || item?.value || "", value: item?.value || item?.label || "" };
    }).filter((item) => item.label);
  }, [value]);

  const selectedLabels = useMemo(
    () => new Set(normalizedValue.map((v) => v.label.toLowerCase())),
    [normalizedValue]
  );

  const suggestions = useMemo(() => {
    const q = inputValue.trim().toLowerCase();
    return SUGGESTED_CATEGORIES.filter((c) => {
      if (selectedLabels.has(c.toLowerCase())) return false;
      if (!q) return true;
      return c.toLowerCase().includes(q);
    });
  }, [inputValue, selectedLabels]);

  const addCategory = (label) => {
    const trimmed = label.trim();
    if (!trimmed) return;
    if (selectedLabels.has(trimmed.toLowerCase())) return;
    onChange([...normalizedValue, { label: trimmed, value: trimmed }]);
    setInputValue("");
  };

  const removeCategory = (label) => {
    onChange(normalizedValue.filter((c) => c.label !== label));
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addCategory(inputValue);
    } else if (e.key === "Backspace" && !inputValue && normalizedValue.length > 0) {
      removeCategory(normalizedValue[normalizedValue.length - 1].label);
    }
  };

  return (
    <div className="cs-wrapper">
      <div className="cs-field" onClick={() => inputRef.current?.focus()}>
        {normalizedValue.map((cat) => (
          <span key={cat.label} className="cs-chip">
            {cat.label}
            <button
              type="button"
              className="cs-chip-remove"
              onClick={(e) => {
                e.stopPropagation();
                removeCategory(cat.label);
              }}
              aria-label={`Remove ${cat.label}`}
            >
              <X size={12} />
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          className="cs-input"
          value={inputValue}
          placeholder={normalizedValue.length === 0 ? placeholder : ""}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 120)}
        />
      </div>

      {open && (suggestions.length > 0 || inputValue.trim()) && (
        <div className="cs-dropdown">
          {suggestions.map((s) => (
            <button
              type="button"
              key={s}
              className="cs-option"
              onMouseDown={(e) => {
                e.preventDefault();
                addCategory(s);
              }}
            >
              {s}
            </button>
          ))}
          {inputValue.trim() && !selectedLabels.has(inputValue.trim().toLowerCase()) && (
            <button
              type="button"
              className="cs-option cs-option--custom"
              onMouseDown={(e) => {
                e.preventDefault();
                addCategory(inputValue);
              }}
            >
              Add "{inputValue.trim()}"
            </button>
          )}
        </div>
      )}
    </div>
  );
}